import {
  Component,
  ViewChildren,
  QueryList,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  AfterViewInit,
} from "@angular/core";
import { EquivalencyService } from "src/app/shared/services/equivalency.service";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatSort, MatSortModule } from "@angular/material/sort";
import {
  MatSelect,
  MatSelectChange,
  MatSelectModule,
} from "@angular/material/select";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Subject, BehaviorSubject, takeUntil, Observable, take } from "rxjs";
import { AppConfigService } from "src/app/services/app-config.service";
import { MatDialog } from "@angular/material/dialog";
import { MmpConfirmPopupComponent } from "../mmp-confirm-popup/mmp-confirm-popup.component";
import { MESSAGES } from "src/app/shared/constants/messages.constant";
import {
  AddCourseDialogComponent,
  AddCourseDialogResult,
} from "src/app/shared/components/add-course-dialog/add-course-dialog.component";
import { DownloadService } from "src/app/shared/services/download.service";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatTabsModule } from "@angular/material/tabs";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MmpAiSummaryComponent } from "../mmp-ai-summary/mmp-ai-summary.component";
import { MmpFeedbackComponent } from "../mmp-feedback/mmp-feedback.component";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MmpFlagComponentComponent } from "../mmp-flag-component/mmp-flag-component.component";
import { CourseOptionRowComponent } from "src/app/shared/components/course-option-row/course-option-row.component";
import { CourseSearchOptionComponent } from "src/app/shared/components/course-search-option/course-search-option.component";

interface EditingCourse {
  course_name?: string;
  matched_course_title?: string;
  matched_course_code?: string;
  grade?: string;
  quarter_credits?: number;
  isEditing?: boolean;
  originalQuarterCredits?: number;
  originalMatchedCourseTitle?: string;
  originalMatchedCourseCode?: string;
  related_courses?: Array<{
    code: string;
    name: string;
  }>;
}

@Component({
  selector: "app-mmp-equivalency",
  templateUrl: "./mmp-equivalency.component.html",
  styleUrls: ["./mmp-equivalency.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTabsModule,
    MatSortModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatTableModule,
    MatTooltipModule,
    MmpAiSummaryComponent,
    MmpFeedbackComponent,
    MmpFlagComponentComponent,
    CourseOptionRowComponent,
    CourseSearchOptionComponent,
  ],
})
export class MmpEquivalencyComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private readonly destroy$ = new Subject<void>();
  currentStage: string = "Equivalency";
  progressTagMsg: string = "In progress";
  aiSummaryLoaded: boolean = false;
  aiSummaryConfidenceScore!: number;
  aiSummaryCourseMatched!: number;
  aiSummaryCourseNotMatched!: number;
  aiSummaryFlaggedCount!: number;
  equivalencyResult: any;
  apiResult: any;
  selectedTabIndex: number = 0;
  universityCourses: any;
  courseSearchText: string = "";
  searchResults: any[] = [];
  addSearchList: any[] = [];
  reportGeneratingValue: boolean = false;

  displayedColumns: string[] = [
    "previousCourse",
    "matchedCourse",
    "grade",
    "quarterCredits",
    "actions",
  ];
  dataSource = new MatTableDataSource<any>([]);
  originalMatchedCourseTitle: string | null = null;
  originalMatchedCourseCode: string | null = null;
  isEditing: boolean = false;
  transcriptAlert: boolean = false;
  finalFeedback: boolean = false;
  showConsoleLogs: boolean = false;
  reportCompleted$!: Observable<boolean>;
  reportFailed$!: Observable<boolean>;
  reportGenerating$!: Observable<boolean>;
  hasEquivalencyEdits: boolean = false;
  editingCourse: EditingCourse | null = null;
  statusMessage: string = "Search by course code, name...";
  addingCourse: boolean = false;
  progressTimeText: string = "Equating...";
  progressTime!: string;
  isHighlightToggleChecked: boolean = false;
  visibleTranscripts: any[] = [];
  ComparisonNormalizer = ComparisonNormalizer;
  readonly MESSAGES = MESSAGES;
  highlightedCourseOption: string | null = null;

  @ViewChildren(MatSort) sorts!: QueryList<MatSort>;
  @Input() selectedTranscriptIndex!: number;
  @Output() selectedTranscriptIndexChange = new EventEmitter<number>();
  @Output() backClick = new EventEmitter<string>();
  @Output() editLockChange = new EventEmitter<boolean>();
  @Output() disableAllTabs = new EventEmitter<boolean>();

  private readonly optionsSubject = new BehaviorSubject<string[]>([]);
  filteredOptions$ = this.optionsSubject.asObservable();

  constructor(
    private readonly equivalencyService: EquivalencyService,
    private readonly snackBar: MatSnackBar,
    private readonly envService: AppConfigService,
    private readonly confirmPopup: MatDialog,
    private readonly downloadService: DownloadService,
  ) {
    this.showConsoleLogs = this.envService.showConsoleLogs;
    this.reportCompleted$ = this.equivalencyService.reportCompleted$;
    this.reportFailed$ = this.equivalencyService.reportFailed$;
    this.reportGenerating$ = this.equivalencyService.reportGenerating$;
  }

  ngOnInit(): void {
    // Subscribe to report generating state for conditional checks
    this.reportGenerating$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.reportGeneratingValue = value;
      });

    const savedData = this.equivalencyService.getEquivalencyData();
    if (savedData) {
      this.equivalencyResult = savedData;
      this.applyHighlightFilterToAllTranscripts();
      this.updateVisibleTranscripts();
    }

    this.equivalencyService.equivalencyResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe((transcripts) => {
        const completed = (transcripts || []).filter(
          (t) => ComparisonNormalizer.string(t.status) === "completed",
        );

        const parsedTranscripts = completed
          .map((t) => {
            let parsed: any = {};
            try {
              parsed = JSON.parse(t.output);
            } catch (e) {
              if (this.showConsoleLogs) {
                console.log("Error parsing transcript output", e);
              }
              parsed = t.output;
            }

            if (!parsed?.result) {
              return null;
            }

            return {
              name: parsed.name,
              credit_summary: parsed.result.credit_summary,
              session_key: parsed.session_key,
            };
          })
          .filter((t) => t !== null);

        if (parsedTranscripts.length === 0) {
          return;
        }

        const mergedTranscripts = this.mergeTranscriptsBySessionKey(
          this.equivalencyResult?.transcripts || [],
          parsedTranscripts,
        );

        this.apiResult = {
          transcripts: mergedTranscripts,
        };
        this.setApiResponse(this.apiResult);
      });

    this.equivalencyService.equivalencySummary$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data?.ai_summary) {
          this.aiSummaryCourseMatched = data.ai_summary.course_matched;
          this.aiSummaryCourseNotMatched = data.ai_summary.courses_not_matched;
          this.aiSummaryFlaggedCount = data.ai_summary.flagged_item_count;
          this.aiSummaryConfidenceScore = data.ai_summary.confidence_score;
          this.progressTime = data.ai_summary.total_processing_time;
          this.progressTagMsg = "Completed";
          this.progressTimeText = "Processing Time:";
          this.aiSummaryLoaded = true;
        }
      });
    if (this.showConsoleLogs) {
      console.log("EquivalencyResult", this.equivalencyResult);
    }
    this.equivalencyService.graphClosingStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        if (status) {
          this.equivalencyService.resetGraphClosingStatus();

          if (this.showConsoleLogs) {
            console.log("GRAPH CLOSE observable triggered");
          }
          this.generatePreEvaluationReport();
          this.showToast(
            "toast-success",
            "Data saved successfully and Pre-evaluation report download initiated",
          );
        }
      });

    // Subscribe to the search results observable
    this.equivalencyService
      .getSearchResults()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (results) => {
          const searchText = this.courseSearchText?.trim().toLowerCase() || "";

          // Filter items from addSearchList that are not already in results
          const filteredAddSearchList = this.addSearchList
            .filter((item) => item.toLowerCase().includes(searchText))
            .filter(
              (item) =>
                !results.some(
                  (result) =>
                    result.trim().toLowerCase() === item.trim().toLowerCase(),
                ),
            );

          const searchResults = [...results, ...filteredAddSearchList];
          this.updateSearchOptions(searchResults);
        },
        (error) => {
          if (this.showConsoleLogs) {
            console.error("Error in course search:", error);
          }
          this.showToast(
            "toast-error",
            MESSAGES.COURSE_PICKER.SEARCH_ERROR_TOAST,
          );

          // Optionally reset status message
          this.statusMessage = MESSAGES.COURSE_PICKER.SEARCH_ERROR_STATUS;
        },
      );

    this.downloadService.download$
      .pipe(takeUntil(this.destroy$))
      .subscribe((url: string) => {
        this.downloadService.downloadFile(url);
      });
  }

  private mergeTranscriptsBySessionKey(
    existingTranscripts: any[],
    incomingTranscripts: any[],
  ): any[] {
    const normalizeSessionKey = (sessionKey: unknown): string =>
      ComparisonNormalizer.string(sessionKey).trim();

    const existingMap = new Map<string, any>();
    (existingTranscripts || []).forEach((transcript: any) => {
      const normalizedKey = normalizeSessionKey(transcript?.session_key);
      if (normalizedKey !== "") {
        existingMap.set(normalizedKey, transcript);
      }
    });

    const merged = (incomingTranscripts || []).map((incoming: any) => {
      const normalizedIncomingKey = normalizeSessionKey(incoming?.session_key);
      const existing = existingMap.get(normalizedIncomingKey);

      if (!existing) {
        return incoming;
      }

      // Preserve in-session user edits when returning to this stage.
      return {
        ...incoming,
        credit_summary: existing.credit_summary || incoming.credit_summary,
      };
    });

    const incomingKeys = new Set(
      (incomingTranscripts || [])
        .map((transcript: any) => normalizeSessionKey(transcript?.session_key))
        .filter((sessionKey: string) => sessionKey !== ""),
    );

    const existingOnly = (existingTranscripts || []).filter(
      (transcript: any) =>
        !incomingKeys.has(normalizeSessionKey(transcript?.session_key)),
    );

    return [...merged, ...existingOnly];
  }

  ngAfterViewInit(): void {
    // Listen for changes in the QueryList of MatSort instances
    this.sorts.changes.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.assignSortToActiveTab();
    });

    // Assign sorts initially (if available)
    this.assignSortToActiveTab();
  }

  private assignSortToActiveTab(): void {
    if (!this.visibleTranscripts?.length || !this.sorts) {
      return;
    }

    const activeTabIndex = this.selectedTranscriptIndex;
    const sort = this.sorts.first;
    const transcript = this.visibleTranscripts[activeTabIndex];

    if (transcript?.dataSource && sort) {
      transcript.dataSource.sort = sort;
    }
  }

  // Check if any row is in edit mode
  isAnyRowEditing(tabIndex: number): boolean {
    if (
      !this.equivalencyResult?.transcripts ||
      !Array.isArray(this.equivalencyResult.transcripts)
    ) {
      return false;
    }

    const transcript =
      this.visibleTranscripts[tabIndex] ??
      this.equivalencyResult.transcripts[tabIndex];
    if (
      !transcript?.dataSource?.data ||
      !Array.isArray(transcript.dataSource.data)
    ) {
      return false;
    }

    const isEditing = transcript.dataSource.data.some(
      (course: any) => !!course.isEditing,
    );

    this.transcriptAlert = isEditing;
    return isEditing;
  }

  onTabChange(event) {
    if (this.isAnyRowEditing(this.selectedTabIndex)) {
      this.transcriptAlert = true;
    } else {
      this.transcriptAlert = false;
      this.selectedTabIndex = event;
      this.assignSortToActiveTab();
    }
  }

  onTabClick(event) {
    this.selectedTranscriptIndexChange.emit(this.selectedTranscriptIndex);
  }

  /** Call this after API response */
  setApiResponse(apiResult: any): void {
    this.equivalencyResult = { ...apiResult };

    if (Array.isArray(this.equivalencyResult.transcripts)) {
      this.equivalencyResult.transcripts =
        this.equivalencyResult.transcripts.map((t: any) => {
          //***create new key related_courses***//
          if (Array.isArray(t.credit_summary)) {
            t.credit_summary = t.credit_summary.map((data: any) => {
              return {
                ...data,
                related_courses: [
                  {
                    code: data.matched_course_code,
                    name: data.matched_course_title,
                  },
                ],
              };
            });
          }
          //***create new key related_courses***//

          const dataSource = new MatTableDataSource(t.credit_summary ?? []);

          dataSource.sortingDataAccessor = (item: any, property: string) => {
            switch (property) {
              case "previousCourse":
                return item.course_name ?? "";
              case "matchedCourse":
                return item.matched_course_title ?? "";
              case "grade":
                return item.grade ?? "";
              case "quarterCredits":
                return item.quarter_credits ?? 0;
              default:
                return "";
            }
          };

          return {
            ...t,
            dataSource,
          };
        });
    }
    this.applyHighlightFilterToAllTranscripts();
    this.updateVisibleTranscripts();

    this.equivalencyService.setEquivalencyData(this.equivalencyResult);
  }

  onHighlightToggleChange(): void {
    this.applyHighlightFilterToAllTranscripts();
    this.updateVisibleTranscripts();
    this.assignSortToActiveTab();
  }

  private applyHighlightFilterToAllTranscripts(): void {
    const transcripts = this.equivalencyResult?.transcripts;
    if (!Array.isArray(transcripts)) {
      return;
    }

    transcripts.forEach((transcript: any) => {
      this.applyHighlightFilterToTranscript(transcript);
    });
  }

  private applyHighlightFilterToTranscript(transcript: any): void {
    if (!transcript?.dataSource) {
      return;
    }

    transcript.dataSource.filterPredicate = (course: any): boolean => {
      if (!this.isHighlightToggleChecked) {
        return true;
      }

      return this.isFlaggedCourse(course);
    };

    if (this.isHighlightToggleChecked) {
      transcript.dataSource.filter = `flagged_${Date.now()}`;
      return;
    }

    transcript.dataSource.filter = "";
  }

  private updateVisibleTranscripts(): void {
    const transcripts = this.equivalencyResult?.transcripts;
    if (!Array.isArray(transcripts)) {
      this.visibleTranscripts = [];
      this.selectedTranscriptIndex = 0;
      return;
    }

    if (this.isHighlightToggleChecked) {
      this.visibleTranscripts = transcripts.filter((transcript: any) => {
        if (!transcript?.dataSource) {
          return false;
        }
        return (transcript.dataSource.filteredData || []).length > 0;
      });
    } else {
      this.visibleTranscripts = transcripts;
    }

    if (this.visibleTranscripts.length === 0) {
      this.selectedTranscriptIndex = 0;
      return;
    }

    if (this.selectedTranscriptIndex >= this.visibleTranscripts.length) {
      this.selectedTranscriptIndex = 0;
      this.selectedTranscriptIndexChange.emit(this.selectedTranscriptIndex);
    }
  }

  private isFlaggedCourse(course: any): boolean {
    const hasNoMatch =
      ComparisonNormalizer.string(course?.matched_course_title).trim() === "";
    const hasMultipleMatches =
      Array.isArray(course?.matched_courses) &&
      course.matched_courses.length > 1;

    return hasNoMatch || hasMultipleMatches;
  }

  goToEvaluationTab() {
    this.backClick.emit("evaluation");
  }

  onMarkComplete() {
    //approval logic comes here
    this.finalFeedback = true;
  }

  isAnyRowMatched(): boolean {
    if (
      !this.equivalencyResult?.transcripts ||
      !Array.isArray(this.equivalencyResult.transcripts)
    ) {
      return false;
    }
    return this.equivalencyResult.transcripts.some((transcript: any) =>
      transcript?.dataSource?.data?.some(
        (course: any) => !!course.matched_course_title,
      ),
    );
  }

  private emitEditLockState(): void {
    this.editLockChange.emit(!!this.editingCourse);
  }

  onEditClick(course: any): void {
    if (this.reportGeneratingValue || !course) {
      return;
    }

    if (course) {
      this.editingCourse = course;
      this.emitEditLockState();
      // Enable edit mode for the course
      course.isEditing = true;
      course.mandatoryMatchedCourseMissing = false;
      course.mandatoryQuarterCreditsMissing = false;
      course.selectedOption = `${course.matched_course_code} - ${course.matched_course_title}`;

      this.courseSearchText = "";
      this.statusMessage = MESSAGES.COURSE_PICKER.DEFAULT_STATUS;
      this.highlightedCourseOption = null;

      const newItem = course.selectedOption;
      const initialOptions = [...this.addSearchList];

      if (this.normalizeCourseOption(newItem) !== "-") {
        const itemExists = initialOptions.some(
          (item) =>
            this.normalizeCourseOption(item) ===
            this.normalizeCourseOption(newItem),
        );
        if (!itemExists) {
          initialOptions.unshift(newItem);
        }
      }

      this.updateSearchOptions(initialOptions);

      // Store the original values to allow reverting changes if needed
      course.originalQuarterCredits = course.quarter_credits;
      course.originalMatchedCourseTitle = course.matched_course_title;
      course.originalMatchedCourseCode = course.matched_course_code;
    }
  }

  onSaveClick(course: any): void {
    const hasMatchedCourse =
      ComparisonNormalizer.string(course?.matched_course_title).trim() !== "" &&
      ComparisonNormalizer.string(course?.matched_course_code).trim() !== "";
    const hasQuarterCredits =
      course?.quarter_credits !== null &&
      course?.quarter_credits !== undefined &&
      `${course.quarter_credits}`.trim() !== "";

    course.mandatoryMatchedCourseMissing = !hasMatchedCourse;
    course.mandatoryQuarterCreditsMissing = !hasQuarterCredits;

    if (!hasMatchedCourse || !hasQuarterCredits) {
      this.showToast(
        "toast-error",
        "Please fill mandatory matched course and quarter credits before saving.",
      );
      return;
    }

    // Save changes and exit edit mode
    this.editingCourse = null;
    this.emitEditLockState();
    course.isEditing = false;
    course.mandatoryMatchedCourseMissing = false;
    course.mandatoryQuarterCreditsMissing = false;

    delete course.originalQuarterCredits;
    delete course.originalMatchedCourseTitle;
    delete course.originalMatchedCourseCode;
    this.transcriptAlert = false;
    this.equivalencyService.setEquivalencyData(this.equivalencyResult);
    this.applyHighlightFilterToAllTranscripts();
    this.updateVisibleTranscripts();

    this.resetCoursePicker();
    this.hasEquivalencyEdits = true;
  }

  onCancelClick(course: any): void {
    // Exit edit mode without saving changes
    course.isEditing = false;
    this.editingCourse = null;
    this.emitEditLockState();

    // reset the course data to its original state
    course.matched_course_title = course.originalMatchedCourseTitle;
    course.matched_course_code = course.originalMatchedCourseCode;
    course.quarter_credits = course.originalQuarterCredits;
    course.mandatoryMatchedCourseMissing = false;
    course.mandatoryQuarterCreditsMissing = false;
    delete course.originalMatchedCourseTitle;
    delete course.originalMatchedCourseCode;
    delete course.originalQuarterCredits;
    this.transcriptAlert = false;
    this.applyHighlightFilterToAllTranscripts();
    this.updateVisibleTranscripts();

    this.resetCoursePicker();
  }

  onCourseSelected(event: MatSelectChange, course: any): void {
    const selectedValue = event.value;
    if (!selectedValue) {
      this.applyCourseSelection(course, "", "", selectedValue);
      return;
    }

    const { code, name } = this.parseCourseSelectionValue(selectedValue);

    this.applyCourseSelection(course, code, name, selectedValue);
  }

  onSearchInput(): void {
    if (this.courseSearchText.trim().length > 2) {
      this.equivalencyService.searchCourses(this.courseSearchText);
      this.highlightedCourseOption = null;
      this.statusMessage = MESSAGES.COURSE_PICKER.SEARCHING_STATUS;
    } else {
      this.statusMessage = MESSAGES.COURSE_PICKER.DEFAULT_STATUS;
    }
  }

  onSearchEnterSelectActive(event: KeyboardEvent): void {
    const activeOption = document.querySelector<HTMLElement>(
      ".mat-mdc-select-panel .mat-mdc-option.mat-mdc-option-active:not(.search-option)",
    );

    if (
      !activeOption ||
      activeOption.getAttribute("aria-disabled") === "true"
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    activeOption.click();
  }

  openAddCourseDialog(course: any, select: MatSelect, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.confirmPopup.open(AddCourseDialogComponent, {
      backdropClass: ["add-course-dialog-backdrop"],
      panelClass: ["add-course-dialog-panel"],
      data: {
        title: MESSAGES.COURSE_PICKER.DIALOG_TITLE,
        courseCodeLabel: MESSAGES.COURSE_PICKER.COURSE_CODE_LABEL,
        courseNameLabel: MESSAGES.COURSE_PICKER.COURSE_NAME_LABEL,
        cancelLabel: MESSAGES.COURSE_PICKER.CANCEL_BUTTON,
        confirmLabel: MESSAGES.COURSE_PICKER.CONFIRM_BUTTON,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result?: AddCourseDialogResult) => {
        if (!result?.code || !result?.name) {
          return;
        }

        this.addNewOption(result.code, result.name, course);
        if (!course?.selectedOption) {
          return;
        }

        this.reopenCoursePicker(select, course.selectedOption);
      });
  }

  addNewOption(code: string, name: string, course: any): void {
    if (!code || !name || code.trim() === "" || name.trim() === "") {
      return; // Ignore empty input
    }

    const trimmedCode = code.trim();
    const trimmedName = name.trim();
    this.addingCourse = true;
    const displayValue = `${trimmedCode} - ${trimmedName}`;

    // Check if the course already exists in optionsSubject or addSearchList
    const normalizedOptions = this.optionsSubject
      .getValue()
      .map((option) => this.normalizeCourseOption(option));
    const normalizedAddSearchList = this.addSearchList.map((item) =>
      this.normalizeCourseOption(item),
    );
    const normalizedInput = this.normalizeCourseOption(displayValue);

    const alreadyExists =
      normalizedOptions.includes(normalizedInput) ||
      normalizedAddSearchList.includes(normalizedInput);

    if (alreadyExists) {
      this.applyCourseSelection(course, trimmedCode, trimmedName, displayValue);
      this.statusMessage = MESSAGES.COURSE_PICKER.EXISTS_STATUS;
    } else {
      this.addSearchList.push(displayValue);
      const current = this.optionsSubject.getValue();
      this.updateSearchOptions([displayValue, ...current]);
      this.applyCourseSelection(course, trimmedCode, trimmedName, displayValue);
      this.statusMessage = MESSAGES.COURSE_PICKER.ADDED_STATUS;
    }

    this.courseSearchText = "";
    this.addingCourse = false;
  }

  private applyCourseSelection(
    course: any,
    code: string,
    name: string,
    displayValue: string,
  ): void {
    if (!course) {
      return;
    }

    if (!displayValue) {
      course.selectedOption = displayValue;
      return;
    }

    course.matched_course_title = name.trim();
    course.matched_course_code = code.trim();
    course.mandatoryMatchedCourseMissing = false;
    course.selectedOption = displayValue;
    this.highlightedCourseOption = displayValue;
  }

  private parseCourseSelectionValue(selectedValue: string): {
    code: string;
    name: string;
  } {
    const separator = " - ";
    const separatorIndex = selectedValue.indexOf(separator);

    if (separatorIndex === -1) {
      return {
        code: selectedValue.trim(),
        name: "",
      };
    }

    return {
      code: selectedValue.slice(0, separatorIndex).trim(),
      name: selectedValue.slice(separatorIndex + separator.length).trim(),
    };
  }

  private reopenCoursePicker(
    select: MatSelect,
    optionToHighlight: string,
  ): void {
    this.highlightedCourseOption = optionToHighlight;

    globalThis.setTimeout(() => {
      select.open();
    }, 0);
  }

  private updateSearchOptions(searchResults: string[]): void {
    this.optionsSubject.next(searchResults);
  }

  private handleAddCourseFailure(
    toastMessage: string,
    statusMessage: string,
  ): void {
    this.showToast("toast-error", toastMessage);
    this.statusMessage = statusMessage;
    globalThis.setTimeout(() => {
      this.addingCourse = false;
    }, 2000);
  }

  private resetCoursePicker(): void {
    this.courseSearchText = "";
    this.statusMessage = MESSAGES.COURSE_PICKER.DEFAULT_STATUS;
    this.highlightedCourseOption = null;
    this.optionsSubject.next([]);
  }

  private normalizeCourseOption(value: string): string {
    return value
      .split("-")
      .map((part) => part.trim())
      .join("-")
      .trim()
      .toLowerCase();
  }

  showToast(type: string, message: string) {
    this.snackBar.open(message, "x", {
      duration: 3000,
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: [type],
    });
  }

  hasNoMatchInTab(tabIndex: number): boolean {
    if (!Array.isArray(this.visibleTranscripts)) {
      return false;
    }

    const transcript = this.visibleTranscripts[tabIndex];
    if (!transcript?.dataSource?.data) {
      return false;
    }

    // Check if any row in the tab has no match or multiple matches.
    return transcript.dataSource.data.some((course: any) =>
      this.isFlaggedCourse(course),
    );
  }

  closeFinalFeedback() {
    this.finalFeedback = false;
  }

  onGeneratePreEvaluationReport() {
    const tooltipMessage = this.getProceedButtonTooltip();
    if (tooltipMessage) {
      this.showToast("toast-error", tooltipMessage);
      return;
    }
    this.equivalencyService.setReportGenerating(true);

    // Check current reportFailed state to determine next action
    this.reportFailed$
      .pipe(take(1))
      .subscribe((failed) => {
        if (failed) {
          this.equivalencyService.setReportFailed(false);
          this.generatePreEvaluationReport();
        } else {
          this.submitEquivalencyData();
        }
      });
  }

  submitEquivalencyData() {
    this.equivalencyResult.transcripts.forEach((transcript) => {
      this.equivalencyService.updateEquivalencyOutput(
        transcript.session_key,
        transcript.credit_summary,
      );
    });
    this.finalGraphClose();
  }

  generatePreEvaluationReport() {
    if (!this.equivalencyResult?.transcripts?.length) {
      this.showToast(
        "toast-error",
        "No transcripts available to generate reports.",
      );
      return;
    }

    // Collect all session_keys as per new WebSocket input contract
    const sessionKeys: string[] = this.equivalencyResult.transcripts
      .map((t: any) => ComparisonNormalizer.string(t.session_key))
      .filter((key: string) => key !== "");

    if (!sessionKeys.length) {
      this.showToast(
        "toast-error",
        "No valid session keys available to generate reports.",
      );
      return;
    }

    this.equivalencyService.setReportGenerating(true);

    this.equivalencyService
      .getPreEvaluationReport(sessionKeys)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          // Handle the response for each session key
          if (this.showConsoleLogs) {
            console.log(
              `Pre-evaluation report response for sessionKeys ${sessionKeys.join(", ")}:`,
              response,
            );
          }

          if (response?.status === "success" && response?.blob_url) {
            this.downloadService.triggerDownload(response.blob_url);
            this.equivalencyService.setReportCompleted(true);
            this.equivalencyService.setReportFailed(false);
            this.equivalencyService.setReportGenerating(false);
            this.showToast(
              "toast-success",
              "Pre-evaluation report is being downloaded.",
            );
          } else {
            this.equivalencyService.setReportFailed(true);
            this.equivalencyService.setReportGenerating(false);
            this.showToast(
              "toast-error",
              "Failed to fetch the report URL. Please try again.",
            );
          }
        },
        (error) => {
          // Handle errors for each session key
          if (this.showConsoleLogs) {
            console.error(
              `Error fetching pre-evaluation report for sessionKeys ${sessionKeys.join(", ")}:`,
              error,
            );
          }
          this.showToast(
            "toast-error",
            "Failed to generate pre-evaluation report. Please try again.",
          );
          this.equivalencyService.setReportFailed(true);
          this.equivalencyService.setReportGenerating(false);
        },
      );
  }

  finalGraphClose() {
    this.equivalencyService.graphClosing();
  }

  isTableDataEmpty(): boolean {
    const transcripts = this.equivalencyResult?.transcripts;

    // No equivalencyResult or no transcripts -> considered empty
    if (!transcripts) {
      return true;
    }

    // Table is empty when there is NO transcript with any filtered data.
    const hasAnyData = transcripts.some(
      (transcript: any) =>
        transcript?.dataSource?.filteredData &&
        transcript.dataSource.filteredData.length > 0,
    );

    return !hasAnyData;
  }

  isAiSummaryCompleted(): boolean {
    return ComparisonNormalizer.string(this.progressTagMsg) === "completed";
  }

  getProceedButtonTooltip(): string {
    if (!this.isAiSummaryCompleted()) {
      return MESSAGES.COURSE_MATCHING.AI_SUMMARY_REQUIRED_FOR_PRE_EVALUATION;
    }
    if (!this.isAnyRowMatched()) {
      return "You must have at least one matched course to generate the report.";
    }
    if (this.isTableDataEmpty()) {
      return "There is no data in the table to generate the report.";
    }
    if (this.reportGeneratingValue) {
      return "The report is currently being generated.";
    }
    if (this.editingCourse) {
      return "Please save or cancel your edits before generating the report.";
    }
    return null;
  }

  ngOnDestroy(): void {
    this.editLockChange.emit(false);
    this.disableAllTabs.emit(false);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
